
import { ServiceLandingPage } from "@/components/service/ServiceLandingPage";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";
import { ContractorsList } from "@/components/ContractorsList";
import { LoadingState } from "@/components/LoadingState";
import { useState, useEffect } from "react";
import { Hero } from "@/components/Hero";

const LandscapingPage = () => {
  const serviceType = "Landscaping";
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  
  // First, query to check what service types actually exist
  const { data: serviceTypes } = useQuery({
    queryKey: ['service-types'],
    queryFn: async () => {
      const { data } = await supabase.rpc('get_unique_service_types');
      return data || [];
    },
  });

  useEffect(() => {
    if (serviceTypes) {
      console.log('Available service types:', serviceTypes);
    }
  }, [serviceTypes]);
  
  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'landscaping'],
    queryFn: async () => {
      console.log('Fetching landscaping contractors...');
      // Try multiple possible service types for landscaping
      const possibleServiceTypes = ['Landscaping', 'Landscaper', 'Lawn Care', 'Lawn Service'];
      
      let allContractors: Contractor[] = [];
      
      // Try each possible service type
      for (const type of possibleServiceTypes) {
        console.log(`Trying service type: ${type}`);
        const { data, error } = await supabase
          .from('contractors')
          .select('*')
          .eq('service_type', type)
          .eq('location', 'oklahoma-city')
          .order('featured', { ascending: false })
          .order('rating', { ascending: false });

        if (error) {
          console.error(`Error fetching contractors with service type ${type}:`, error);
          continue;
        }

        if (data && data.length > 0) {
          console.log(`Found ${data.length} contractors with service type ${type}`);
          allContractors = [...allContractors, ...data];
        }
      }

      // If no contractors found with any of the specific service types,
      // try a broader search using ilike for partial matches
      if (allContractors.length === 0) {
        console.log('No exact matches found, trying partial match with ilike');
        const { data, error } = await supabase
          .from('contractors')
          .select('*')
          .ilike('service_type', '%landscap%')
          .eq('location', 'oklahoma-city')
          .order('featured', { ascending: false })
          .order('rating', { ascending: false });

        if (error) {
          console.error('Error with partial match query:', error);
        } else if (data && data.length > 0) {
          console.log(`Found ${data.length} contractors with partial match`);
          allContractors = data;
        }
      }

      console.log(`Total contractors found: ${allContractors.length}`);
      return allContractors as Contractor[];
    },
    retry: 1,
  });

  const features = [
    {
      title: "Landscape Design",
      description: "Custom design services to create beautiful and functional outdoor spaces"
    },
    {
      title: "Lawn Maintenance",
      description: "Regular mowing, edging, fertilization, and weed control services"
    },
    {
      title: "Irrigation Systems",
      description: "Installation and repair of efficient irrigation systems"
    },
    {
      title: "Hardscaping",
      description: "Professional installation of patios, walkways, retaining walls, and more"
    },
    {
      title: "Tree & Shrub Care",
      description: "Planting, trimming, and maintenance for healthy trees and shrubs"
    }
  ];

  const processSteps = [
    {
      title: "Initial Consultation",
      description: "Meet with a landscaping professional to discuss your vision and needs"
    },
    {
      title: "Customized Plan",
      description: "Receive a detailed proposal with design concepts and pricing"
    },
    {
      title: "Professional Installation",
      description: "Experienced crews implement your landscape project with precision"
    },
    {
      title: "Final Walkthrough",
      description: "Review the completed work to ensure your complete satisfaction"
    }
  ];

  const benefits = [
    {
      title: "Increased Property Value",
      description: "Professional landscaping can significantly boost your home's market value"
    },
    {
      title: "Enhanced Curb Appeal",
      description: "Create an impressive first impression for visitors and potential buyers"
    },
    {
      title: "Local Climate Expertise",
      description: "Work with professionals who understand Oklahoma's specific soil and climate conditions"
    },
    {
      title: "Time Savings",
      description: "Reclaim your weekends while professionals maintain your landscape"
    },
    {
      title: "Sustainable Solutions",
      description: "Environmentally friendly approaches to create beautiful and resilient landscapes"
    }
  ];

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: "Landscaping", href: "/landscaping-services-oklahoma-city" },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Landscaping Services in Oklahoma City",
    "description": "Professional landscaping services in Oklahoma City for residential and commercial properties.",
    "provider": {
      "@type": "Organization",
      "name": "Home Pros OKC"
    },
    "areaServed": {
      "@type": "City",
      "name": "Oklahoma City"
    },
    "serviceType": "Landscaping"
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Landscaping Services in Oklahoma City | Home Pros OKC"
        description="Professional landscaping and lawn care services in Oklahoma City. Creating beautiful outdoor spaces for homes and businesses."
        canonicalUrl="/landscaping-services-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title="Landscaping Services in Oklahoma City"
          subtitle="Transform your outdoor space with our network of experienced landscaping professionals in Oklahoma City."
          buttonText="Get a Free Quote"
          buttonLink="#quote-form"
        />

        <ServiceLandingPage
          title="Landscaping Services in Oklahoma City"
          metaDescription="Professional landscaping and lawn care services in Oklahoma City. Creating beautiful outdoor spaces for homes and businesses."
          introText="Transform your outdoor space with our network of experienced landscaping professionals in Oklahoma City."
          features={features}
          processSteps={processSteps}
          benefits={benefits}
          ctaText="Ready to enhance your outdoor space?"
          ctaButtonText="Get Connected"
          contactText="Fill out the form below to connect with top-rated landscapers in OKC."
        />

        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top Landscaping Professionals in Oklahoma City</h2>
          
          {isLoading ? (
            <LoadingState message="Loading landscaping professionals..." />
          ) : contractors && contractors.length > 0 ? (
            <>
              <p className="mb-4 text-muted-foreground">
                Found {contractors.length} landscaping professionals serving Oklahoma City.
              </p>
              <ContractorsList
                contractors={contractors}
                isLoading={isLoading}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onServiceTypeChange={() => {}}
                onLocationChange={() => {}}
                selectedServiceType="Landscaping"
              />
            </>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              We're currently updating our list of landscaping professionals in Oklahoma City. 
              Please check back soon or use the form above to request a quote.
            </p>
          )}
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold mb-4">Looking for Top-Rated Landscaping Services?</h3>
          <p className="mb-6">
            View our curated list of the best landscaping contractors in Oklahoma City.
          </p>
          <a 
            href="/best-landscapers-okc" 
            className="inline-block px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition"
          >
            See Top-Rated Landscapers
          </a>
        </div>
      </div>
    </div>
  );
};

export default LandscapingPage;
